import React, { useEffect } from 'react';
import { Helmet as MetaTags } from 'react-helmet';
import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import ContentContainerDefault from '~/components/molecule/ContentContainer';
import MasterDetail from '~/components/template/MasterDetail';
import TEST_ID_MASTER_DETAIL from '~/components/template/MasterDetail/index.testid';
import createPageTitle from '~/util/createPageTitle';
import Settings from './components/Settings';
import AboutUs from './components/AboutUs';
import useApp from '~/hooks/useApp';
import Loading from '~/components/atom/Loading';
import AppErrorScreen from '~/components/template/AppErrorScreen';
import NotFound from '../../404';
import {
  useUpdateAppStatusMutation,
  type AppStatusFields_AppStatus_Biedwijzer_Fragment,
} from '~/graphql/types';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import { atom, useRecoilState } from 'recoil';
import removeTypenames from '~/util/removeTypenames';
import { equals, omit } from 'ramda';
import findDifference from '~/util/findDifference';
import formatToastMessage from '~/util/formatToastMessage';
import useAddToast from '~/hooks/useAddToast';
import Report from './components/Report';
import AddressSearch from './components/AddressSearch';
import FloatingSaveBar from '~/components/molecule/FloatingSaveBar';
import Catalog from '~/Catalog';
import asContentInput from './utils/asContentInput';

const text = {
  successMessage: 'Instellingen opgeslagen',
  errorMessage: 'Er is iets mis gegaan bij het opslaan van de instellingen',
};

type Props = RouteComponentProps & {};

export type BiedwijzerAppStatus = AppStatusFields_AppStatus_Biedwijzer_Fragment;

export const biedwijzerContentState = atom<BiedwijzerAppStatus | null>({
  key: 'biedwijzerContentState',
  default: null,
});
export const biedwijzerInitialContentState = atom<BiedwijzerAppStatus | null>({
  key: 'biedwijzerInitialContentState',
  default: null,
});

const Biedwijzer: React.FCC<Props> = ({ uri = '' }) => {
  const { app, loading, error } = useApp('AppStatus_Biedwijzer');

  const { id: accountId } = useCurrentAccount();
  const addToast = useAddToast();

  const [updateAppStatus, { loading: updating }] = useUpdateAppStatusMutation();

  const [initialValues, setInitialValues] = useRecoilState(
    biedwijzerInitialContentState,
  );
  const [currentValues, setCurrentValues] = useRecoilState(
    biedwijzerContentState,
  );
  const changeCount = getChangeCount(initialValues, currentValues);

  useEffect(() => {
    if (app?.appStatus && app.appStatus.__typename === 'AppStatus_Biedwijzer') {
      setCurrentValues(app.appStatus);
      setInitialValues(app.appStatus);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [app?.appStatus]);

  if (loading) return <Loading />;
  if (error) return <AppErrorScreen />;
  if (!app?.appStatus.enabled) return <NotFound />;

  const onSave = async () => {
    if (!app?.appStatus || app.appStatus.__typename !== 'AppStatus_Biedwijzer')
      return;
    if (!initialValues || !currentValues) return;

    await updateAppStatus({
      variables: {
        accountId,
        update: {
          AppStatus_Biedwijzer: {
            ...removeTypenames(
              omit(
                ['locked', 'permission', 'widgetAppConfigurationItems'],
                currentValues,
              ),
            ),
            content: asContentInput(currentValues.content),
          },
        },
      },
    }).then(({ data, errors }) => {
      if (errors && errors.length > 0) {
        return addToast([
          formatToastMessage(Catalog.genericUnknownErrorMessage, 'danger'),
        ]);
      }
      if (data) {
        if (data.updateAppStatus.__typename === 'AppStatus_Biedwijzer') {
          setInitialValues(data.updateAppStatus);
          setCurrentValues(data.updateAppStatus);
        }
        addToast([formatToastMessage(text.successMessage, 'success')]);
      }
    });
  };

  return (
    <>
      <MetaTags>
        <title>{createPageTitle('Biedwijzer')}</title>
      </MetaTags>
      <ContentContainerDefault
        breadcrumbs={[
          {
            to: '/-/apps',
            label: 'Apps',
          },
          { label: 'BiedWijzer' },
        ]}
      >
        <MasterDetail
          basePath={uri}
          dataTestId={TEST_ID_MASTER_DETAIL.CONTAINER}
          navbar={[
            {
              type: 'link',
              to: '',
              icon: 'gear',
              name: 'Instellingen',
            },
            { type: 'separator' },
            { type: 'heading', title: "Pagina's" },
            {
              type: 'link',
              to: '/address-search',
              icon: 'map-pin',
              name: 'Adres zoeken',
            },
            {
              type: 'link',
              to: '/about-us',
              icon: 'user',
              name: 'Over jouw aankoopdiensten',
            },
            {
              type: 'link',
              to: '/report',
              icon: 'file-text',
              name: 'Rapport',
            },
          ]}
        >
          <Settings path="/*" />
          <AddressSearch path="/address-search" />
          <AboutUs path="/about-us" />
          <Report path="/report" />
        </MasterDetail>

        <FloatingSaveBar
          visible={changeCount !== 0}
          actionProps={{
            onCancel: () => {
              setCurrentValues(initialValues);
            },
            onSave: () => onSave(),
            disabled: loading || updating,
          }}
          numberOfChanges={changeCount}
        />
      </ContentContainerDefault>
    </>
  );
};

const getChangeCount = (
  initialValues: BiedwijzerAppStatus | null,
  currentValues: BiedwijzerAppStatus | null,
) => {
  const res = findDifference({
    obj1: removeTypenames(initialValues || {}),
    obj2: removeTypenames(currentValues || {}),
    fieldsToOmit: [
      ['widgetAppConfigurationItems'],
      ['officeNumbers'],
      ['content', 'addressSearch', 'image', 'url'],
      ['content', 'aboutUs', 'image', 'url'],
    ],
  });

  const officeNumbersChanged = equals(
    initialValues?.officeNumbers || [],
    currentValues?.officeNumbers || [],
  )
    ? 0
    : 1;

  const imageFields = ['addressSearch', 'aboutUs'];
  imageFields.forEach(field => {
    const prevValue =
      initialValues?.content?.[field].image?.__typename === 'AssetImage';
    const currentValue =
      currentValues?.content?.[field].image?.__typename === 'DHImage';
    if (prevValue && currentValue) {
      res.differenceCount += 1;
    }
  });

  return res.differenceCount + officeNumbersChanged;
};

export default Biedwijzer;
